import { copyDefaults, type CopyKey } from "./copy.ts";
import { copyEsMX } from "./locales/es-MX.ts";

/* -------------------------------------------------------------------------- */
/* Locales                                                                     */
/*                                                                             */
/* A brand names its default locale (`locale`, the language of `copy`,         */
/* `tagline`, `fitTags` and `meta`) and the locales it offers (`locales`).     */
/* Text for the other locales lives in `i18n[<locale>]`; the core UI ships a   */
/* catalog per locale in `./locales/`. Empty strings anywhere in a non-default */
/* locale mean "not translated yet" and fall through.                          */
/* -------------------------------------------------------------------------- */

/** A brand's own text in one non-default locale (schema: `BrandLocaleText` in index.ts). */
interface LocaleText {
  copy?: Record<string, string>;
  tagline?: string;
  fitTags?: { label?: string; refPlaceholder?: string; namePlaceholder?: string; disclaimer?: string };
  meta?: { description?: string };
  legal?: { privacy?: string; terms?: string; attribution?: string };
}

/** The slice of `BrandConfig` locale handling reads (structural, so this module doesn't import the schema). */
interface LocaleConfig {
  locale: string;
  locales: string[];
  copy: Record<string, string>;
  i18n: Record<string, LocaleText>;
  tagline: string;
  fitTags: { label: string; refPlaceholder: string; namePlaceholder: string; disclaimer?: string };
  meta?: { description?: string };
  legal?: { privacy?: string; terms?: string; attribution?: string };
}

/** Core UI catalogs by locale. English is the source (`copyDefaults`). */
export const coreCopy: Record<string, Partial<Record<CopyKey, string>>> = {
  en: copyDefaults,
  "es-MX": copyEsMX,
};

/** Native names for the language switcher (shown in their own language). */
export const localeNames: Record<string, string> = {
  en: "English",
  "es-MX": "Español",
};

/** Display name of a locale in its own language: `localeNames`, then Intl, then the tag. */
export function localeName(locale: string): string {
  if (localeNames[locale]) return localeNames[locale];
  try {
    const name = new Intl.DisplayNames([locale], { type: "language" }).of(locale);
    if (name) return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  } catch {
    // unknown tag
  }
  return locale;
}

/** Locales the brand offers, default first, without duplicates. One entry = no switcher. */
export function brandLocales(config: Pick<LocaleConfig, "locale" | "locales">): string[] {
  return [...new Set([config.locale, ...config.locales])];
}

const lang = (tag: string) => tag.toLowerCase().split(/[-_]/)[0];

/**
 * Best offered locale for a list of requested tags (e.g. `navigator.languages`),
 * in order of preference: an exact match (case-insensitive) first, else the
 * first offered locale with the same language ("es-AR" or "es" -> "es-MX").
 */
export function matchLocale(available: readonly string[], requested: readonly string[]): string | undefined {
  for (const r of requested) {
    if (!r) continue;
    const exact = available.find((a) => a.toLowerCase() === r.toLowerCase().replace(/_/g, "-"));
    if (exact) return exact;
    const sameLang = available.find((a) => lang(a) === lang(r));
    if (sameLang) return sameLang;
  }
  return undefined;
}

/**
 * The locale to show: a stored choice the brand still offers, else the best
 * match for the browser's languages, else the brand default.
 */
export function pickLocale(opts: { available: readonly string[]; fallback: string; stored?: string | null; preferred?: readonly string[] }): string {
  const { available, fallback, stored, preferred = [] } = opts;
  if (stored) {
    const hit = available.find((a) => a.toLowerCase() === stored.toLowerCase());
    if (hit) return hit;
  }
  return matchLocale(available, preferred) ?? (available.includes(fallback) ? fallback : (available[0] ?? fallback));
}

function coreCatalog(locale: string): Partial<Record<CopyKey, string>> | undefined {
  if (coreCopy[locale]) return coreCopy[locale];
  const key = matchLocale(Object.keys(coreCopy), [locale]);
  return key ? coreCopy[key] : undefined;
}

type CopyVars = Record<string, string | number>;

/** A copy lookup bound to one locale. Brand-only keys (e.g. "bb.footer.note") are accepted too. */
export interface Copy {
  (key: CopyKey | (string & {}), vars?: CopyVars): string;
  /** The locale this lookup resolves for. */
  readonly locale: string;
  /** Whether the brand overrides `key` (for this locale or its default). */
  overridden(key: CopyKey | (string & {})): boolean;
}

/**
 * Copy for one locale. Resolution order for a key:
 *
 * - default locale: brand `copy` (an empty string here is honoured), then the
 *   core catalog of that locale, then core English;
 * - any other locale L: brand `i18n[L].copy`, core catalog for L, brand
 *   `copy` (default locale), core catalog for the default locale, core English.
 *   Empty strings are skipped at every step.
 *
 * So a brand's English wording ("Brackets") never replaces a translated core
 * string in Spanish; it shows only while Spanish has nothing for that key.
 */
export function makeCopy(config: Pick<LocaleConfig, "copy"> & Partial<Pick<LocaleConfig, "locale" | "i18n">>, locale?: string): Copy {
  const def = config.locale ?? "en";
  const active = locale ?? def;
  const brandDefault: Record<string, string> = { ...config.copy, ...(config.i18n?.[def]?.copy ?? {}) };
  const isDefault = active === def;
  const chain: Record<string, string | undefined>[] = isDefault
    ? [coreCatalog(def) ?? {}, copyDefaults]
    : [config.i18n?.[active]?.copy ?? {}, coreCatalog(active) ?? {}, brandDefault, coreCatalog(def) ?? {}, copyDefaults];
  const resolve = (key: string): string => {
    if (isDefault && brandDefault[key] !== undefined) return brandDefault[key];
    for (const c of chain) {
      const s = c[key];
      if (s) return s;
    }
    const base = (copyDefaults as Record<string, string>)[key];
    return base ?? key;
  };
  const fn = ((key: string, vars: CopyVars = {}) => {
    let s = resolve(key);
    for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
    return s;
  }) as Copy;
  Object.defineProperty(fn, "locale", { value: active });
  fn.overridden = (key) => (!isDefault && !!config.i18n?.[active]?.copy?.[key]) || brandDefault[key] !== undefined;
  return fn;
}

/**
 * The brand config as seen in `locale`: `tagline`, `fitTags` texts and
 * `meta.description` come from `i18n[locale]` where set (non-empty), else stay
 * as authored for the default locale. `copy` is left alone (use `makeCopy`).
 */
export function localizeBrandConfig<C extends LocaleConfig>(config: C, locale: string): C {
  const t = config.i18n[locale];
  if (!t) return config;
  const pick = (v: string | undefined, base: string) => (v ? v : base);
  const fitTags = { ...config.fitTags };
  for (const k of ["label", "refPlaceholder", "namePlaceholder"] as const) fitTags[k] = pick(t.fitTags?.[k], fitTags[k]);
  if (t.fitTags?.disclaimer) fitTags.disclaimer = t.fitTags.disclaimer;
  const meta = config.meta && t.meta?.description ? { ...config.meta, description: t.meta.description } : config.meta;
  const legal = config.legal && { ...config.legal };
  if (legal) for (const k of ["privacy", "terms", "attribution"] as const) if (t.legal?.[k]) legal[k] = t.legal[k];
  return { ...config, tagline: pick(t.tagline, config.tagline), fitTags, meta, legal };
}
